<template>
    <span v-if="visible">
        <b-button class="align-baseline" title="Email" v-b-modal="emailModalId" variant="link">
            <font-awesome-icon :icon="['fas', 'envelope']"></font-awesome-icon>
        </b-button>
        <b-button
            class="align-baseline"
            title="Record Payment"
            v-b-modal="recordPaymentModalId"
            variant="link"
            :disabled="isRecordPaymentButtonDisabled"
        >
            <font-awesome-icon :icon="['fas', 'dollar-sign']"></font-awesome-icon>
        </b-button>
        <b-button
            class="align-baseline"
            title="Process Payment"
            v-b-modal="hasRecurringSubscription ? processPaymentModalId : noCreditCardModalId"
            variant="link"
            :disabled="isProcessPaymentButtonDisabled"
        >
            <font-awesome-icon :icon="['fas', 'credit-card']"></font-awesome-icon>
        </b-button>

        <validation-observer ref="email_form" v-slot="{ handleSubmit, meta }">
            <modal
                :id="emailModalId"
                title="Email Subscriber"
                @shown="onEmailModalShown"
                ok-title="Send"
                :ok-disabled="!meta.valid || loading"
                @ok.prevent="handleSubmit(_, sendEmail)"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-form @submit.prevent="handleSubmit(_, sendEmail)">
                    <p>To: {{ subscriberEmail }}</p>
                    <p>
                        <validation-provider name="subject" v-slot="{ errors, handleChange }" rules="required">
                            <form-input
                                v-model="emailSubject"
                                label="Subject:"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </p>
                    <p>
                        <validation-provider name="body" v-slot="{ errors, handleChange }" rules="required">
                            <form-textarea
                                v-model="emailBody"
                                label="Body:"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-textarea>
                        </validation-provider>
                    </p>
                    <p>
                        <form-checkbox v-model="emailAddToNotices"
                            >Publish in Subscriber Notices</form-checkbox
                        >
                    </p>
                </b-form>
            </modal>
        </validation-observer>

        <validation-observer ref="record_payment_form" v-slot="{ handleSubmit, meta }">
            <modal
                :id="recordPaymentModalId"
                title="Record Payment"
                @shown="onRecordPaymentModalShown"
                ok-title="Record Payment"
                :ok-disabled="!meta.valid || loading"
                @ok.prevent="handleSubmit(_, recordPayment)"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-form @submit.prevent="handleSubmit(_, recordPayment)">
                    <p>
                        <validation-provider
                            name="transaction_amount"
                            v-slot="{ errors, handleChange }"
                            rules="validFloat|required"
                        >
                            <form-input
                                v-model="recordPaymentAmount"
                                label="Amount:"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </p>
                    <p>
                        <form-input v-model="recordPaymentTransactionCode" label="Transaction Code:"></form-input>
                    </p>
                    <p>
                        <validation-provider name="record_payment_date" v-slot="{ errors, handleChange }">
                            <form-datepicker
                                v-model="recordPaymentDate"
                                label="Date:"
                                :hide-header="true"
                                :display-format="userUnicodeDateFormat"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-datepicker>
                        </validation-provider>
                    </p>
                </b-form>
            </modal>
        </validation-observer>

        <validation-observer ref="process_payment_form" v-slot="{ handleSubmit, meta }">
            <modal
                :id="processPaymentModalId"
                title="Process Payment"
                @shown="onProcessPaymentModalShown"
                ok-title="Process Payment"
                :ok-disabled="!meta.valid || loading"
                @ok.prevent="handleSubmit(_, processPayment)"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-form @submit.prevent="handleSubmit(_, processPayment)">
                    <p>
                        <validation-provider
                            name="process_payment_amount"
                            v-slot="{ errors, handleChange }"
                            rules="validFloat|required"
                        >
                            <form-input
                                v-model="processPaymentAmount"
                                label="Amount:"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </p>
                    <p>
                        Card Type: {{ cardType }}<br />
                        Expiration Date: {{ cardExp }}<br />
                        Card Holder: {{ cardHolder }}
                    </p>
                </b-form>
            </modal>
        </validation-observer>

        <modal :id="noCreditCardModalId" title="Info">
            No credit card assigned to subscriber: {{ subscriberEmail }}
        </modal>
    </span>
</template>

<script>
import { mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import API from '../../../api';
import Modal from '../../generic/modal/modal.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
import { formatResponseError } from '../../../utils/api';
import { SERVER_DATE_FORMAT } from '../../../constants';

export default {
    components: {
        Modal,
        BusyModal,
        FormInput,
        FormTextarea,
        FormCheckbox,
        FormDatepicker,
    },
    data() {
        return {
            loading: false,
            subscriberId: 0,
            visible: false,
            emailModalId: '',
            emailSubject: '',
            emailBody: '',
            emailAddToNotices: false,
            recordPaymentModalId: '',
            recordPaymentTransactionCode: '',
            recordPaymentDate: format(new Date(), SERVER_DATE_FORMAT),
            recordPaymentAmount: 0,
            processPaymentModalId: '',
            processPaymentAmount: 0,
            noCreditCardModalId: '',
            hasRecurringSubscription: false,
            canRecordPayment: false,
            canProcessPayment: false,
            subscriberEmail: '',
            cardType: '',
            cardExp: '',
            cardHolder: '',
            commissionIds: [],
        };
    },
    created() {
        this.subscriberId = this.params.data.subscriber_id;
        this.visible = !!this.subscriberId;
        this.emailModalId = `modal-email-${this.subscriberId}`;
        this.recordPaymentModalId = `modal-record-payment-${this.subscriberId}`;
        this.processPaymentModalId = `modal-process-payment-${this.subscriberId}`;
        (this.processPaymentInfoModalId = `modal-process-payment-info-${this.subscriberId}`),
            (this.noCreditCardModalId = `modal-no-credit-card-${this.subscriberId}`);
        this.hasRecurringSubscription = this.params.data.has_recurring_subscription;
        this.canRecordPayment = this.params.data.can_record_payment;
        this.canProcessPayment = this.params.data.can_process_payment;
        this.subscriberEmail = this.params.data.subscriber_email;
        this.cardType = this.params.data.card_type;
        this.cardExp = this.params.data.card_exp;
        this.cardHolder = this.params.data.card_holder;
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        isRecordPaymentButtonDisabled() {
            return !this.canRecordPayment;
        },
        isProcessPaymentButtonDisabled() {
            return !this.canProcessPayment;
        },
    },
    methods: {
        onEmailModalShown() {
            this.emailSubject = '';
            this.emailBody = '';
            this.emailAddToNotices = false;
        },
        onRecordPaymentModalShown() {
            this.recordPaymentTransactionCode = '';
            this.recordPaymentAmount = this.params.data.total_total_fee_usd;
            this.commissionIds = this.params.data.commission_ids;
            this.recordPaymentDate = format(new Date(), SERVER_DATE_FORMAT);
            this.$refs.record_payment_form.setValues({
                transaction_amount: this.recordPaymentAmount,
                record_payment_date: this.commissionIds,
            });
        },
        onProcessPaymentModalShown() {
            this.commissionIds = this.params.data.commission_ids;
            this.processPaymentAmount = this.params.data.total_total_fee_usd;
            this.cardType = this.params.data.card_type;
            this.cardExp = this.params.data.card_exp;
            this.cardHolder = this.params.data.card_holder;
            this.$refs.process_payment_form.setValues({
                process_payment_amount: this.processPaymentAmount,
            });
        },
        async sendEmail() {
            this.loading = true;
            try {
                await API.post('/app/admin/channel-billing/channel-billing/send-email/', {
                    subscriber_id: this.subscriberId,
                    subject: this.emailSubject,
                    body: this.emailBody,
                    add_to_notices: this.emailAddToNotices,
                });
                this.$bvModal.hide(this.emailModalId);
                this.successToast({ text: 'Email sent.' });
            } catch (err) {
                const { generic, fields } = formatResponseError(err);

                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.email_form.setErrors(fields);
                }
            } finally {
                this.loading = false;
            }
        },
        async recordPayment() {
            this.loading = true;
            try {
                await API.post('/app/admin/channel-billing/channel-billing/record-payment/', {
                    subscriber_id: this.subscriberId,
                    amount: this.recordPaymentAmount,
                    transaction_code: this.recordPaymentTransactionCode,
                    commission_ids: this.commissionIds,
                    payment_date: format(parseISO(this.recordPaymentDate), SERVER_DATE_FORMAT),
                });
                this.$bvModal.hide(this.recordPaymentModalId);
                this.params.onRecordPaymentSuccess && this.params.onRecordPaymentSuccess();
            } catch (err) {
                const { generic, fields } = formatResponseError(err);

                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.record_payment_form.setErrors(fields);

                    const hasSubscriberIdError = Object.keys(fields).some((field) => field === 'subscriber_id');
                    if (!!hasSubscriberIdError) {
                        this.errorToast({ text: `Subscriber ID (${this.subscriberId}) is not valid.` });
                    }

                    const hasCommissionIdsError = Object.keys(fields).some((field) => field === 'commission_ids');
                    if (!!hasCommissionIdsError) {
                        this.errorToast({ text: `Commission IDs (${this.commissionIds.join(', ')}) are not valid.` });
                    }
                }
            } finally {
                this.loading = false;
            }
        },
        async processPayment() {
            this.loading = true;
            try {
                let response = await API.post('/app/admin/channel-billing/channel-billing/process-payment/', {
                    subscriber_id: this.subscriberId,
                    amount: this.processPaymentAmount,
                    commission_ids: this.commissionIds,
                });
                this.params.onProcessPaymentResponse &&
                    this.params.onProcessPaymentResponse({
                        message: response.data,
                    });
                this.$bvModal.hide(this.processPaymentModalId);
            } catch (err) {
                const { generic, fields } = formatResponseError(err);

                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.process_payment_form.setErrors(fields);

                    const hasSubscriberIdError = Object.keys(fields).some((field) => field === 'subscriber_id');
                    if (!!hasSubscriberIdError) {
                        this.errorToast({ text: `Subscriber ID (${this.subscriberId}) is not valid.` });
                    }

                    const hasCommissionIdsError = Object.keys(fields).some((field) => field === 'commission_ids');
                    if (!!hasCommissionIdsError) {
                        this.errorToast({ text: `Commission IDs (${this.commissionIds.join(', ')}) are not valid.` });
                    }
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
